import React from 'react';
import { usePhone } from '../PhoneContext';
import { normalizePhoneNumber } from '../phoneNumber';

/**
 * PhoneLink — makes the number itself click-to-call.
 *
 * The team's ask was to click the number, not hunt for an icon beside it.
 * Renders an <a href="tel:…"> so it behaves like a real link (middle-click,
 * copy address, and a working call on mobile), but when the CRM softphone
 * is registered we intercept the click and dial in-app instead of handing
 * off to the OS.
 *
 * The href is always the normalized form, so a number displayed as
 * "050-989-1010" hands "0509891010" to the device dialer.
 *
 *   <PhoneLink number={customer.phone} />
 *   <PhoneLink number={c.phone} className="text-xs" />
 */
export const PhoneLink: React.FC<{
  number: string | null | undefined;
  /** Text to show instead of the raw number (defaults to the number). */
  children?: React.ReactNode;
  className?: string;
  /** Render nothing at all when there's no number (default shows '—'). */
  hideWhenEmpty?: boolean;
}> = ({ number, children, className = '', hideWhenEmpty = false }) => {
  const { startCall, registration, call } = usePhone();

  const raw = (number || '').trim();
  if (!raw) return hideWhenEmpty ? null : <span className="text-slate-400">—</span>;

  const dialable = normalizePhoneNumber(raw) || raw;
  // Busy on an existing call, or the softphone isn't up — fall through to
  // the tel: href so the desktop/mobile handler can still take it.
  const canDialInApp = registration === 'registered' && !call;

  const onClick = (e: React.MouseEvent) => {
    if (!canDialInApp) return;      // let the browser follow tel:
    e.preventDefault();
    e.stopPropagation();            // never trigger the surrounding row
    startCall(dialable);
  };

  return (
    <a
      href={`tel:${dialable}`}
      onClick={onClick}
      title={canDialInApp ? `התקשר ל-${raw}` : `חייג ${raw}`}
      // dir=ltr so a number always reads left-to-right inside the RTL page.
      dir="ltr"
      className={`hover:text-primary hover:underline underline-offset-2 transition cursor-pointer ${className}`}
    >
      {children ?? raw}
    </a>
  );
};
